import { useRef } from 'react';
import ReactJson from 'react-json-view';
import clsx from 'clsx';
// mantine
import {
    ActionIcon,
    Highlight,
    Table,
    Tabs,
    UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// icons
import { ChevronRight } from 'lucide-react';
// components
import type { ILog, ICallLog, ILevel, TPersona } from '@newo/api/gateway';
import LogItem from '@/modules/Analytics/components/SystemOutput/components/LogItem';
// utils
import usePromptDrawerStore from '@/modules/PromptDrawer/PromptDrawer.store.ts';
import { linkify } from '@/utils';
// assets
import styles from '@/modules/Analytics/components/SystemOutput/components/CallLogItem/CallLogItem.module.css';

interface CallLogItemProps {
    agent?: TPersona;
    datetime: string;
    level: ILevel;
    message: string;
    log: ILog & ICallLog;
    searchTerm?: string;
    onGoto?: () => void;
}

const CallLogItem = ({
    agent,
    datetime,
    level,
    message,
    log,
    searchTerm
}: CallLogItemProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpened, { toggle }] = useDisclosure(false);
    const handleClick = () => {
        toggle();
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const prompt =
        log.data.parameters.find(({ name }) => name === 'prompt')?.value ?? '';
    const schema = log.data.parameters.find(
        ({ name }) => name === 'jsonSchema'
    )?.value;

    const handlePrompt = () => {
        usePromptDrawerStore.getState().selectPrompt({
            prompt,
            result: log.data.result,
            schema
        });
    };

    const isGen = ['gen', 'genstream'].includes(log.data.name.toLowerCase());

    return (
        <LogItem datetime={datetime} level={level} ref={ref} type="call">
            <div className="flex gap-2">
                <ActionIcon
                    color="gray"
                    size="xs"
                    variant="light"
                    onClick={handleClick}
                >
                    <ChevronRight
                        className={clsx({ [styles.rotated]: isOpened })}
                    />
                </ActionIcon>
                <div className={styles.message}>
                    {log.project_idn ? (
                        <span className="whitespace-nowrap pr-1 font-bold text-gray-7">
                            [{log.project_idn}]
                        </span>
                    ) : undefined}
                    {agent ? (
                        <span className="whitespace-nowrap pr-1 font-bold text-blue-6">
                            [{agent.name}]
                        </span>
                    ) : undefined}
                    {log.data.context.flow_idn ? (
                        <span className="whitespace-nowrap pr-1 font-bold text-gray-7">
                            [{log.data.context.flow_idn}]
                        </span>
                    ) : undefined}
                    {log.data.source_type === 'skill' ? (
                        <span className="whitespace-nowrap pr-1 font-bold text-gray-7">
                            [{log.data.source.skill_idn}]
                        </span>
                    ) : undefined}
                    {log.data.source_type === 'skill' ? (
                        <span className="whitespace-nowrap pr-1 text-blue-6">
                            [line: {log.data.source.call_position.line}]
                        </span>
                    ) : undefined}
                    <div
                        className={clsx({
                            'text-blue-6': isGen,
                            'text-green-8': !!log.data.prompt_script
                        })}
                    >
                        {searchTerm ? (
                            <Highlight
                                className="whitespace-normal"
                                highlight={searchTerm}
                                lineClamp={1}
                                size="xs"
                            >
                                {message}
                            </Highlight>
                        ) : (
                            <div
                                className="line-clamp-1"
                                dangerouslySetInnerHTML={{
                                    __html: linkify(message)
                                }}
                            />
                        )}
                    </div>
                    {!!prompt && (
                        <UnstyledButton
                            className={styles.promptButton}
                            onClick={handlePrompt}
                        >
                            Show Prompt
                        </UnstyledButton>
                    )}
                </div>
            </div>
            {isOpened ? (
                <Tabs className="mt-2" defaultValue="custom">
                    <Tabs.List className="text-gray-8">
                        <Tabs.Tab value="custom">Details</Tabs.Tab>
                        <Tabs.Tab value="json">JSON</Tabs.Tab>
                        <Tabs.Tab value="states">States</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel className="py-4" value="custom">
                        <div className={styles.grid}>
                            <div>runtime_context_id</div>
                            <div>{log.data.runtime_context_id}</div>
                            <div>external_event_id</div>
                            <div>{log.data.external_event_id}</div>
                            <div>result</div>
                            <div>{log.data.result}</div>
                        </div>
                    </Tabs.Panel>
                    <Tabs.Panel className="py-4" value="json">
                        <ReactJson
                            displayDataTypes={false}
                            displayObjectSize={false}
                            name={false}
                            src={log.data}
                        />
                    </Tabs.Panel>
                    <Tabs.Panel className="py-4" value="states">
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th className="w-44">State</Table.Th>
                                    <Table.Th>Value</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {log.data.context.states.map((state) => (
                                    <Table.Tr key={state.name}>
                                        <Table.Td>{state.name}</Table.Td>
                                        <Table.Td>{state.value}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Tabs.Panel>
                </Tabs>
            ) : undefined}
        </LogItem>
    );
};

export default CallLogItem;
